/**
 * BarcodeDemoPanel — C081 바코드/QR 데모 (queue #646 §R646-2 항목6)
 * QR/Code128 + 텍스트 + 크기 입력 → PNG 인라인 렌더(base64 data-uri). 순수.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBarcodeGenerate } from '../../hooks/useBarcodeDemo';
import type { BarcodeGenerateRequest } from '../../types/showcase';
import { cardStyle, sectionTitleStyle, subtitleStyle, labelStyle, inputStyle, primaryButtonStyle, DemoErrorState, DemoEmptyHint } from './demoShared';

export function BarcodeDemoPanel() {
  const { t } = useTranslation();
  const generateMutation = useBarcodeGenerate();
  const [type, setType] = useState<BarcodeGenerateRequest['type']>('QR');
  const [text, setText] = useState('https://eraf.skax.co.kr');
  const [size, setSize] = useState(256); // BE CommonShowcaseBarcodeDemoService 기본값과 동일

  function handleGenerate() {
    generateMutation.mutate({ type, text, size });
  }

  return (
    <section aria-labelledby="barcode-panel-title">
      <h2 id="barcode-panel-title" style={sectionTitleStyle}>{t('showcase.demo.barcode.title')}</h2>
      <p style={subtitleStyle}>{t('showcase.demo.barcode.subtitle')}</p>

      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
          <div>
            <label htmlFor="barcode-type" style={labelStyle}>{t('showcase.demo.barcode.typeLabel')}</label>
            <select id="barcode-type" value={type} onChange={(e) => setType(e.target.value as BarcodeGenerateRequest['type'])} style={inputStyle}>
              <option value="QR">QR</option>
              <option value="CODE128">CODE128</option>
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label htmlFor="barcode-text" style={labelStyle}>{t('showcase.demo.barcode.textLabel')}</label>
            <input id="barcode-text" type="text" value={text} onChange={(e) => setText(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label htmlFor="barcode-size" style={labelStyle}>{t('showcase.demo.barcode.sizeLabel')}</label>
            <input id="barcode-size" type="number" min={50} max={1000} value={size} onChange={(e) => setSize(Number(e.target.value))} style={inputStyle} />
          </div>
        </div>

        <button
          type="button"
          onClick={handleGenerate}
          disabled={generateMutation.isPending || !text.trim()}
          aria-busy={generateMutation.isPending}
          style={{ ...primaryButtonStyle, opacity: generateMutation.isPending ? 0.6 : 1 }}
        >
          {generateMutation.isPending ? t('showcase.demo.barcode.generating') : t('showcase.demo.barcode.generateButton')}
        </button>

        <div style={{ marginTop: 'var(--spacing-4)' }}>
          {generateMutation.isError && (
            <DemoErrorState title={t('showcase.demo.common.callFailed')} error={generateMutation.error} />
          )}
          {!generateMutation.isError && !generateMutation.data && <DemoEmptyHint text={t('showcase.demo.barcode.empty')} />}
          {generateMutation.data && !generateMutation.isError && (
            <img
              src={`data:${generateMutation.data.contentType};base64,${generateMutation.data.base64}`}
              alt={t('showcase.demo.barcode.resultAlt')}
              style={{ border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-surface-background)', padding: 'var(--spacing-3)' }}
            />
          )}
        </div>
      </div>
    </section>
  );
}
